import {
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from "@mui/material";
import { useIntl } from "react-intl";
import BlockIcon from "@mui/icons-material/Block";
import useBoolean from "hooks/useBoolean";
import useGlobalContext from "hooks/globalData";
import { useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { ResponseCode } from "services/KnowfullyAdminClient";
import { Color } from "types/colors";

type ImportsSummaryDetailsProps = {
  organizationName?: string;
  jobTypeName?: string | undefined;
  createdOn?: string | undefined;
  jobName?: string | undefined;
  jobStatusName?: string | undefined;
};

export function ImportsSummaryDetails(
  summary: ImportsSummaryDetailsProps
) {
  const { formatMessage: t } = useIntl();
  const [jobCancelling, setJobCancelling] = useBoolean(false);
  const { migrationJobService } = useGlobalContext();
  const queryClient = useQueryClient();

  const { jobId } = useParams<{
    jobId: string;
  }>();

  const onCancelJobData = async () => {
    setJobCancelling.setTrue();
    var response = await migrationJobService.cancelJob(jobId!);

    if (response.responseCode === ResponseCode.OK) {
      queryClient.invalidateQueries(["importJobDataSummary", jobId!]);
      queryClient.invalidateQueries(["importJobSummary", jobId!]);

      queryClient.invalidateQueries(["importJobProcessSnapshot", jobId!]);
    }

    setJobCancelling.setFalse();
  };

  const rows = [
    {
      title: t({ id: "organization" }),
      component: <span>{summary.organizationName}</span>,
    },
    {
      title: t({ id: "jobTypeLabel" }),
      component: summary?.jobTypeName && <span>{summary?.jobTypeName}</span>,
    },
    {
      title: t({ id: "uploadDate" }),
      component: summary?.createdOn && <span>{summary?.createdOn}</span>,
    },
    {
      title: t({ id: "nameLabel" }),
      component: summary?.jobName && <span>{summary?.jobName}</span>,
    },
    {
      title: t({ id: "jobStatusLabel" }),
      component: summary?.jobStatusName && (
        <span>{summary?.jobStatusName}</span>
      ),
    },
    {
      title: t({ id: "cancelJob" }),
      component: (
        <IconButton
          size="small"
          style={{ color: Color.Red }}
          onClick={() => {
            onCancelJobData();
          }}
        >
          {jobCancelling ? <CircularProgress size={28} /> : <BlockIcon />}
        </IconButton>
      ),
    },
  ].filter((r) => !!r.component);

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableBody>
            {rows.map(({ title, component }, i) => (
              <TableRow key={i}>
                <TableCell
                  component="th"
                  scope="row"
                  style={{ fontWeight: "bold", whiteSpace: "nowrap" }}
                >
                  {title}
                </TableCell>
                <TableCell width="100%">{component}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
